// ======================================================
// axiosConfig.js – Cliente HTTP centralizado
// Proyecto: Evalúa Salud IPS
// Autor: Breyner Javier Montalvo Peñarredonda
// ======================================================

import axios from "axios";

/* ======================================================
   CONFIG BASE
   ------------------------------------------------------
   REACT_APP_API_URL debe terminar en /api.
   En produccion debe apuntar al backend publicado por
   Nginx, no al puerto interno 4000.
====================================================== */

const getBrowserOrigin = () => {
  if (typeof window === "undefined" || !window.location?.origin) {
    return "";
  }

  return window.location.origin;
};

const resolveDefaultApiUrl = () => {
  if (process.env.NODE_ENV === "development") {
    return "http://localhost:4000/api";
  }

  const origin = getBrowserOrigin();
  return origin ? `${origin}/api` : "/api";
};

const RAW_API_URL = process.env.REACT_APP_API_URL || resolveDefaultApiUrl();

const API_URL = RAW_API_URL.replace(/\/+$/, "");

const RAW_BACKEND_ORIGIN =
  process.env.REACT_APP_BACKEND_ORIGIN ||
  (RAW_API_URL === "/api" ? getBrowserOrigin() : RAW_API_URL.replace(/\/?api$/, ""));

const BACKEND_ORIGIN = RAW_BACKEND_ORIGIN.replace(/\/+$/, "");

const PUBLIC_ASSET_PATHS = [
  /^\/activo\/[0-9a-fA-F]{24}\/?$/,
  /^\/activos\/[0-9a-fA-F]{24}\/?$/,
  /^\/vehiculo\/[0-9a-fA-F]{24}\/?$/
];

export const isPublicQrPath = (pathname = "") =>
  PUBLIC_ASSET_PATHS.some((pattern) => pattern.test(String(pathname || "")));

/* ======================================================
   🧱 INSTANCIA AXIOS
====================================================== */

const api = axios.create({
  baseURL: API_URL,
  timeout: 20000,
  headers: {
    Accept: "application/json"
  }
});

/* ======================================================
   🔐 REQUEST INTERCEPTOR
====================================================== */

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    } else {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* ======================================================
   ⚙ RESPONSE INTERCEPTOR
====================================================== */

api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const status = error?.response?.status || null;
    const url = error?.config?.url || "";
    const method = (error?.config?.method || "").toUpperCase();

    let mensaje =
      error?.response?.data?.mensaje ||
      error?.response?.data?.error ||
      error?.message ||
      "Error desconocido.";

    const detalles = error?.response?.data?.detalles;
    if (Array.isArray(detalles) && detalles.length > 0) {
      mensaje = detalles.join(" | ");
    }

    if (error?.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        mensaje = parsed?.mensaje || mensaje;
        error.response.data = parsed;
      } catch (parseErr) {
        console.warn("No se pudo parsear Blob JSON:", parseErr);
      }
    }

    if (error.code === "ECONNABORTED") {
      console.error("Timeout del servidor");
    }

    if (!error.response) {
      console.error("Sin respuesta del servidor");
      return Promise.reject(error);
    }

    if (status === 401) {
      console.warn("Sesion expirada:", mensaje);
      localStorage.removeItem("token");

      const pathname = window.location?.pathname || "";
      if (!window.location.pathname.includes("/login") && !isPublicQrPath(pathname)) {
        window.location.href = "/login";
      }
    }

    if (status >= 500) {
      console.error(`Error interno [${status}] -> ${method} ${url}: ${mensaje}`);
    }

    if (status >= 400 && status < 500) {
      console.warn(`Error del cliente [${status}] -> ${method} ${url}: ${mensaje}`);
    }

    return Promise.reject(error);
  }
);

/* ======================================================
   🔧 UTILIDADES
====================================================== */

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem("token", token);
  } else {
    localStorage.removeItem("token");
  }
};

export const buildUploadUrl = (relativePath = "") => {
  const raw = String(relativePath || "").trim();

  if (!raw) {
    return "";
  }

  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) {
    return raw;
  }

  const clean = raw.replace(/^\/+/, "").replace(/^uploads\//, "");

  return `${BACKEND_ORIGIN}/uploads/${clean}`;
};

export const buildUploadAssetDeleteReference = (
  reference,
  fallbackDirectory = ""
) => {
  const rawValue =
    typeof reference === "string"
      ? reference
      : reference?.rutaRelativa ||
        reference?.archivoPath ||
        reference?.nombreArchivo ||
        reference?.url ||
        "";

  const value = String(rawValue || "")
    .trim()
    .replace(/^['"]+|['"]+$/g, "");

  if (!value || value === "[object Object]") {
    return "";
  }

  if (
    (value.startsWith("{") && value.endsWith("}")) ||
    (value.startsWith("\"") && value.endsWith("\""))
  ) {
    try {
      const parsed = JSON.parse(value);
      return buildUploadAssetDeleteReference(parsed, fallbackDirectory);
    } catch (_error) {
      // Continúa con estrategia tolerante.
    }
  }

  const fallback = String(fallbackDirectory || "")
    .trim()
    .replace(/^\/+|\/+$/g, "");

  const normalizePath = (candidate = "") => {
    const clean = String(candidate || "")
      .trim()
      .replace(/^\/+/, "")
      .replace(/^uploads\//i, "")
      .replace(/[?#].*$/, "");

    if (!clean) {
      return "";
    }

    if (fallback) {
      const marker = `${fallback}/`;
      const index = clean.toLowerCase().lastIndexOf(marker.toLowerCase());
      if (index >= 0) {
        return clean.slice(index);
      }
    }

    return clean;
  };

  if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value);
      return normalizePath(decodeURIComponent(parsed.pathname || ""));
    } catch (_error) {
      return normalizePath(value);
    }
  }

  return normalizePath(value);
};

/* ======================================================
   EXPORTS
====================================================== */

export { API_URL, BACKEND_ORIGIN };
export default api;

/* ======================================================
   🧪 DEBUG
====================================================== */

if (process.env.NODE_ENV !== "production") {
  // eslint-disable-next-line no-console
  console.log(
    "=============================================\n" +
      "   Axios configurado correctamente\n" +
      `   API_URL: ${API_URL}\n` +
      `   BACKEND_ORIGIN: ${BACKEND_ORIGIN}\n` +
      "   JWT + interceptores activos\n" +
      "   Timeout: 20s\n" +
      "============================================="
  );
}
